import { useEffect, useState, useCallback } from 'react';
import {
  Stack,
  Typography,
  Toolbar,
  Divider,
  Button,
  IconButton,
  Paper,
  Box,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import { useApi } from '@/providers/ApiContext';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate, useLocation } from 'react-router';
import { LoadingButton } from '@mui/lab';
import INSPECTION_TYPES from '@/components/inspectionTypes';
import {
  getInspection,
  updateInspection,
  createInspection,
} from '@/actions/inspection';
import { getManifest } from '@/actions/manifest';
import InspectionNameField from './InspectionNameField';
import InspectionDocumentTypeField from './InspectionDocumentTypeField';
import InspectionManifestsField from './InspectionManifestsField';
import InspectionDocumentsField from './InspectionDocumentsField';
import InspectionAutoRunField from './InspectionAutoRunField';
import DocumentUploader from '@/components/shared/DocumentUploader';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import INSPECTION_STATUSES from '@/components/inspectionStatuses';
import type { Inspection } from '@/types/apiTypes';
import type { DocumentType } from '@/types/documentTypes';
import type { Manifest } from '@/types/apiTypes';
import type { Document } from '@/types/apiTypes';
import type { DocumentUpload } from '@/types/sharedTypes';
import type { EntityId } from '@/types/apiTypes';

interface InspectionFormProps {
  type: INSPECTION_TYPES;
}

function InspectionForm({ type }: InspectionFormProps): React.JSX.Element {
  const user = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const [inspection, setInspection] = useState<
    (Inspection & { [key: string]: unknown }) | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pristine, setPristine] = useState<boolean>(true);
  const [name, setName] = useState<string>('');
  const [name_error, setNameError] = useState<boolean | string>(false);
  const [document_type, setDocumentType] = useState<DocumentType | null>(null);
  const [manifests, setManifests] = useState<Manifest[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [doc_content, setDocContent] = useState<DocumentUpload | undefined>(
    undefined,
  );
  const [doc_title, setDocTitle] = useState<string | undefined>(undefined);
  const [document_error, setDocumentError] = useState<string | undefined>(
    undefined,
  );
  const [global_manifest, setGlobalManifest] = useState<Manifest | null>(null);
  const [api_error, setApiError] = useState<string | null>(null);
  const [auto_run, setAutoRun] = useState<boolean>(false);

  const sp = new URLSearchParams(location.search);
  const inspection_id = sp.get('inspection_id');

  const base_path =
    type === INSPECTION_TYPES.EFFECTIVE
      ? '/quartermaster/internalAudit'
      : '/quartermaster/evaluator';

  useEffect(() => {
    if (user?.entity_id && !inspection && api) {
      if (inspection_id) {
        getInspection(api, inspection_id)
          .then((data) => {
            setInspection(data as Inspection & { [key: string]: unknown });
            setName(data?.name || '');
            setDocumentType({
              entity_id: (data as Inspection & { document_type_id?: EntityId })
                ?.document_type_id,
            } as DocumentType);
            setManifests(
              (
                data as Inspection & {
                  manifests?: Array<{
                    reference_document_content_id?: string;
                    manifest_entity_id?: EntityId;
                    manifest_name?: string;
                  }>;
                }
              )?.manifests
                ?.filter(
                  (manifest) => manifest.reference_document_content_id, // global manifest should be only one with no reference doc
                )
                .map((manifest) => {
                  return {
                    entity_id: manifest.manifest_entity_id || '',
                    name: manifest.manifest_name || '',
                  } as Manifest;
                }) || [],
            );
            setDocuments(
              (
                data as Inspection & {
                  documents?: Array<{
                    document_entity_id?: EntityId;
                    document_name?: string;
                  }>;
                }
              )?.documents?.map((document) => {
                return {
                  entity_id: document.document_entity_id || '',
                  name: document.document_name || '',
                } as Document;
              }) || [],
            );
            setAutoRun(
              (data as Inspection & { auto_run?: boolean })?.auto_run ?? false,
            );
          })
          .catch((error) => {
            console.log(error);
            navigate('/error', {
              state: {
                errorMessage: 'Failed to load inspection for editing',
                errorDetails:
                  error.message ||
                  'Unable to retrieve the inspection for editing. This could be due to network issues, server problems, or the inspection may no longer exist.',
                returnPath: `/quartermaster/${type === INSPECTION_TYPES.EFFECTIVE ? 'internalAudit' : 'evaluator'}`,
              },
            });
          });
      }
    }
  }, [user?.entity_id, inspection_id, inspection, api, navigate, type]);

  useEffect(() => {
    if (type === INSPECTION_TYPES.PRELIMINARY && !global_manifest && api) {
      getManifest(api, 'global')
        .then((data) => {
          setGlobalManifest(data);
        })
        .catch((error) => {
          console.log(error);
          navigate('/error', {
            state: {
              errorMessage: 'Failed to load global manifest',
              errorDetails:
                error.message ||
                'Unable to retrieve the global manifest. This could be due to network issues or server problems.',
              returnPath: `/quartermaster/${(type as INSPECTION_TYPES) === INSPECTION_TYPES.EFFECTIVE ? 'internalAudit' : 'evaluator'}`,
            },
          });
        });
    }
  }, [type, global_manifest, api, navigate]);

  const handleDocumentUpload = useCallback(
    async (files: DocumentUpload[]) => {
      if (files[0]) {
        const contents = files[0];
        setDocContent({
          ...contents,
          type: contents.mime_type,
        });
        setDocTitle(files[0].title);
        if (!Boolean(name)) {
          setName(files[0].title);
        }
      }
    },
    [name, setName],
  );

  const handleSubmit = async (): Promise<void> => {
    try {
      setLoading(true);

      if (!document_type) {
        setApiError('Document type is required');
        setLoading(false);
        return;
      }

      const payload: {
        name: string;
        type: INSPECTION_TYPES;
        document_type_id: EntityId;
        manifests: EntityId[];
        documents: EntityId[];
        draft?: DocumentUpload;
        auto_run: boolean;
      } = {
        name,
        type,
        document_type_id: document_type.entity_id,
        manifests: manifests.map((manifest) => manifest.entity_id),
        documents: documents.map((document) => document.entity_id),
        auto_run,
      };
      if (type === INSPECTION_TYPES.PRELIMINARY && global_manifest) {
        payload.manifests.unshift(global_manifest.entity_id);
      }

      if (!api) return;
      let updated_inspection: Inspection;
      if (inspection_id && inspection) {
        const inspectionWithStatus = inspection as Inspection & {
          status?: string;
        };
        updated_inspection = await updateInspection(
          api,
          inspection.entity_id,
          inspectionWithStatus.status === INSPECTION_STATUSES.DRAFT
            ? payload
            : { name },
        );
      } else {
        if (doc_content) {
          payload.draft = doc_content;
          console.log(payload);
          updated_inspection = await createInspection(api, payload);
        } else {
          updated_inspection = await createInspection(api, payload);
        }
      }
      navigate(`${base_path}/${updated_inspection.entity_id}`);
    } catch (error) {
      console.error('Error saving inspection:', error);
      setApiError(
        (error as Error).message ||
          'Unable to save the inspection. This could be due to network issues, server problems, or invalid data.',
      );
      setLoading(false);
    }
  };

  if (!user?.entity_id || (!inspection && inspection_id)) {
    return <LoadingMask />;
  }

  return (
    <Grid container>
      <Grid size={12}>
        <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
          <Toolbar
            sx={{
              minHeight: '55px !important',
            }}
          >
            <IconButton sx={{ mr: 2 }} onClick={() => navigate(-1)}>
              <ArrowBackOutlinedIcon />
            </IconButton>
            <Typography variant='h6' sx={{ mr: 4 }}>
              {inspection ? 'Edit Inspection' : 'New Inspection'}
            </Typography>
          </Toolbar>
        </Paper>
      </Grid>
      <Divider />
      <Grid size={12}>
        <Box
          padding='15px'
          sx={{
            maxWidth: 600,
          }}
        >
          <Grid container spacing={2} style={{ padding: '15px' }}>
            {type === INSPECTION_TYPES.EFFECTIVE ? (
              <>
                <Grid size={12}>
                  <InspectionNameField
                    value={name}
                    setValue={setName}
                    error={name_error}
                    setError={setNameError}
                    setChanged={() => setPristine(false)}
                  />
                </Grid>
                <Grid size={12}>
                  <InspectionDocumentTypeField
                    inspection_type={type}
                    value={
                      document_type
                        ? { entity_id: document_type.entity_id }
                        : undefined
                    }
                    setValue={(value) => setDocumentType(value ?? null)}
                    setChanged={() => setPristine(false)}
                    edit={Boolean(inspection)}
                  />
                </Grid>
                {document_type ? (
                  <>
                    <Grid size={12}>
                      <InspectionManifestsField
                        value={manifests}
                        setValue={setManifests}
                        document_type_id={document_type.entity_id}
                        setChanged={() => setPristine(false)}
                        edit={Boolean(inspection)}
                      />
                    </Grid>
                    <Grid size={12}>
                      <InspectionDocumentsField
                        value={documents}
                        setValue={setDocuments}
                        document_type_id={document_type.entity_id}
                        setChanged={() => setPristine(false)}
                        edit={Boolean(inspection)}
                      />
                    </Grid>
                    <Grid size={12}>
                      <InspectionAutoRunField
                        value={auto_run}
                        setValue={setAutoRun}
                        setChanged={() => setPristine(false)}
                        edit={Boolean(inspection)}
                      />
                    </Grid>
                  </>
                ) : undefined}
              </>
            ) : (
              <>
                <Grid size={12}>
                  <DocumentUploader
                    setDocuments={handleDocumentUpload}
                    disabled={Boolean(inspection)}
                    tooltip='Select and upload a quality control document to begin inspection.'
                  />
                </Grid>
                {doc_content ||
                (inspection as Inspection & { doc_content_id?: string })
                  ?.doc_content_id ? (
                  <>
                    {!Boolean(inspection) ? (
                      <Grid size={12}>
                        <Typography
                          variant='subtitle1'
                          sx={{ wordBreak: 'break-all' }}
                        >
                          Uploaded <b>{doc_title}</b>
                        </Typography>
                      </Grid>
                    ) : undefined}
                    <Grid size={12}>
                      <Divider />
                    </Grid>
                    <Grid size={12}>
                      <InspectionNameField
                        value={name}
                        setValue={setName}
                        error={name_error}
                        setError={setNameError}
                        setChanged={() => setPristine(false)}
                      />
                    </Grid>
                    <Grid size={12}>
                      <InspectionDocumentTypeField
                        inspection_type={type}
                        value={
                          document_type
                            ? { entity_id: document_type.entity_id }
                            : undefined
                        }
                        setValue={(value) => setDocumentType(value ?? null)}
                        setChanged={() => setPristine(false)}
                        edit={Boolean(inspection)}
                      />
                    </Grid>
                  </>
                ) : undefined}
                {document_type ? (
                  <>
                    <Grid size={12}>
                      <InspectionManifestsField
                        value={manifests}
                        setValue={setManifests}
                        document_type_id={document_type.entity_id}
                        setChanged={() => setPristine(false)}
                        edit={Boolean(inspection)}
                      />
                    </Grid>
                    <Grid size={12}>
                      <InspectionAutoRunField
                        value={auto_run}
                        setValue={setAutoRun}
                        setChanged={() => setPristine(false)}
                        edit={Boolean(inspection)}
                      />
                    </Grid>
                  </>
                ) : undefined}
              </>
            )}
          </Grid>
        </Box>

        <Grid size={12}>
          <Divider />
        </Grid>
        <Grid size={12}>
          <Toolbar>
            <Stack spacing={2} direction='row'>
              <LoadingButton
                variant='contained'
                onClick={handleSubmit}
                loading={loading}
                disabled={
                  inspection_id
                    ? pristine || Boolean(name_error) || !Boolean(name)
                    : pristine ||
                      Boolean(name_error) ||
                      !Boolean(name) ||
                      !Boolean(document_type) ||
                      !manifests?.length
                }
              >
                Submit
              </LoadingButton>

              <Button
                variant='outlined'
                color='inherit'
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Cancel
              </Button>
            </Stack>
          </Toolbar>
        </Grid>
      </Grid>
      <ErrorSnackBar
        message={document_error}
        onClear={() => {
          setDocContent(undefined);
          setDocumentError(undefined);
        }}
      />

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Grid>
  );
}

export default InspectionForm;
